var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useRef } from 'react';
import { SearchContainer, SearchForm, SuggestionsContainer, SuggestionItem, SearchIcon, ClearButton } from './Search.style';
import { InputText } from '../../atoms/InputText/InputText';
import { highlightTerm } from './HighlightTerm';
export const Search = ({ id = "search-component", componentPath = "", ariaLabel: _ariaLabel = "Buscar", searchResultPage = "", placeholder = "O que você procura?", className, disabled = false, onSearch, onSuggestionClick, fetchSuggestions, colorVariant = "default", inverse = true, value, onChange }) => {
    const [internalSearchTerm, setInternalSearchTerm] = useState("");
    const isControlled = value !== undefined;
    const searchTerm = isControlled ? value : internalSearchTerm;
    const [suggestions, setSuggestions] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const searchBarRef = useRef(null);
    const handleFetchSuggestions = useCallback((term) => __awaiter(void 0, void 0, void 0, function* () {
        if (term.length < 3) {
            setSuggestions([]);
            return;
        }
        try {
            if (fetchSuggestions) {
                const results = yield fetchSuggestions(term);
                setSuggestions(results);
            }
            else if (componentPath) {
                const response = yield fetch(`${componentPath}.suggestions.json?term=${encodeURIComponent(term)}`);
                if (!response.ok) {
                    console.error(`HTTP error! Status: ${response.status}`);
                    return;
                }
                const data = yield response.json();
                setSuggestions(data);
            }
        }
        catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
        }
    }), [componentPath, fetchSuggestions]);
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleFetchSuggestions(searchTerm);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm, handleFetchSuggestions]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchBarRef.current &&
                !searchBarRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);
    const handleFormSubmit = (event) => {
        event.preventDefault();
        if (searchTerm.trim()) {
            if (onSearch) {
                onSearch(searchTerm.trim());
            }
            else if (searchResultPage) {
                const searchUrl = `${searchResultPage}?term=${encodeURIComponent(searchTerm.trim())}&page=1&sort=score`;
                window.location.href = searchUrl;
            }
            setIsFocused(false);
        }
    };
    const handleInputChange = (event) => {
        if (!isControlled) {
            setInternalSearchTerm(event.target.value);
        }
        if (onChange) {
            onChange(event);
        }
    };
    const handleInputFocus = () => {
        setIsFocused(true);
    };
    const handleSuggestionSelect = (suggestion) => {
        if (!isControlled) {
            setInternalSearchTerm(suggestion);
        }
        // If controlled, we can't update value directly, but we can trigger onChange?
        // Or we assume parent handles it via onSuggestionClick if needed?
        // Standard pattern: if controlled, value prop must update.
        // We can emit a fake change event or just let parent handle via onSuggestionClick.
        // However, onSuggestionClick is void.
        setIsFocused(false);
        if (onSuggestionClick) {
            onSuggestionClick(suggestion);
        }
        else if (searchResultPage) {
            // eslint-disable-next-line
            window.location.href = `${searchResultPage}?term=${encodeURIComponent(suggestion)}`;
        }
    };
    const handleClear = () => {
        if (!isControlled) {
            setInternalSearchTerm("");
        }
        // Same issue for controlled: how to clear?
        // We should probably expose onClear or let parent handle via onChange("")?
        // For now, let's trigger onChange with empty string if possible.
        if (onChange) {
            // Mock event
            const event = {
                target: { value: "" }
            };
            onChange(event);
        }
        setSuggestions([]);
        setIsFocused(false);
    };
    const SearchIconSvg = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
    const CloseIconSvg = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M18 6L6 18M6 6L18 18", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
    return (_jsxs(SearchContainer, { ref: searchBarRef, className: `ps-search-bar${isFocused ? " focus" : ""}${disabled ? " disabled" : ""} ${className || ""}`, id: id, isFocused: isFocused, colorVariant: colorVariant, children: [_jsx(SearchForm, { onSubmit: handleFormSubmit, children: _jsx(InputText, { type: "text", placeholder: placeholder, value: searchTerm, onChange: handleInputChange, onFocus: handleInputFocus, disabled: disabled, role: "combobox", "aria-controls": "search-suggestions", "aria-autocomplete": "list", "aria-expanded": suggestions.length > 0, customIcon: !searchTerm ? (_jsx(SearchIcon, { colorVariant: colorVariant, inverse: inverse, children: _jsx(SearchIconSvg, {}) })) : (_jsx(ClearButton, { type: "button", onClick: handleClear, "aria-label": "Limpar busca", disabled: disabled, colorVariant: colorVariant, children: _jsx(CloseIconSvg, {}) })) }) }), suggestions.length > 0 && isFocused && (_jsx(SuggestionsContainer, { className: "ps-search-bar-autocomplete", colorVariant: colorVariant, children: _jsx("div", { className: "ps-search-bar-autocomplete-scroll", children: suggestions.map((suggestion, index) => (_jsx(SuggestionItem, { onClick: () => handleSuggestionSelect(suggestion), colorVariant: colorVariant, dangerouslySetInnerHTML: {
                            __html: highlightTerm ? highlightTerm(suggestion, searchTerm) : suggestion
                        } }, index))) }) }))] }));
};
//# sourceMappingURL=Search.js.map